import { useEffect, useState, useCallback } from "react";
import {
  LayoutDashboard, ListChecks, FolderOpen, MessageSquare, GraduationCap,
  Send, FileText, Plus, ExternalLink, UserPlus, Upload, Download, Link2,
  Trash2, Check, Circle, CheckCircle2, ArrowUpRight, Inbox,
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

export const PROJECT_STATUS = [
  { id: "brief", label: "Brief" },
  { id: "design", label: "Design" },
  { id: "developpement", label: "Développement" },
  { id: "livraison", label: "Livraison" },
  { id: "termine", label: "Terminé" },
];

export const DEVIS_STATUS = [
  { id: "aucun", label: "Aucun devis" },
  { id: "envoye", label: "Devis envoyé" },
  { id: "accepte", label: "Devis accepté" },
  { id: "acompte_a_payer", label: "Acompte à payer" },
  { id: "acompte_paye", label: "Acompte payé" },
  { id: "solde_a_payer", label: "Solde à payer" },
  { id: "solde_paye", label: "Soldé" },
];
export const DEVIS_PAID = ["acompte_paye", "solde_paye"];
export const DEVIS_DUE = ["acompte_a_payer", "solde_a_payer"];

const STAGE_NEXT = { a_faire: "en_cours", en_cours: "termine", termine: "a_faire" };
const STAGE_LABEL = { a_faire: "À faire", en_cours: "En cours", termine: "Terminé" };
const STAGE_COLOR = {
  a_faire: "text-zinc-500 border-white/10",
  en_cours: "text-[var(--accent)] border-[var(--accent)]/40",
  termine: "text-emerald-400 border-emerald-400/40",
};

const NAV = [
  { id: "apercu", label: "Aperçu", desc: "Avancement & activité", icon: LayoutDashboard },
  { id: "afaire", label: "À faire", desc: "Vos actions", icon: ListChecks },
  { id: "fichiers", label: "Fichiers", desc: "Livrables & demandes", icon: FolderOpen },
  { id: "messages", label: "Messagerie", desc: "Échanges avec l'équipe", icon: MessageSquare },
  { id: "priseenmain", label: "Prise en main", desc: "Guides & aide", icon: GraduationCap },
];

function fmt(ts) {
  try { return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function human(bytes) {
  if (!bytes) return "";
  const u = ["o", "Ko", "Mo", "Go"]; let i = 0; let n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function ProjectPanel({ project, profile, isAdmin, canManage = isAdmin, canValidate = false }) {
  const [tab, setTab] = useState("apercu");
  const [stages, setStages] = useState([]);
  const [actions, setActions] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [fileRequests, setFileRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [activity, setActivity] = useState([]);
  const seenKey = `ml_seen_${project.id}`;
  const [seenAt, setSeenAt] = useState(() => { try { return Number(localStorage.getItem(seenKey)) || 0; } catch { return 0; } });
  const [status, setStatus] = useState(project.status);
  const [siteUrl, setSiteUrl] = useState(project.site_url || "");
  const [devisStatus, setDevisStatus] = useState(project.devis_status || "aucun");

  const [msg, setMsg] = useState("");
  const [newAction, setNewAction] = useState("");
  const [newLink, setNewLink] = useState({ name: "", url: "" });
  const [devEmail, setDevEmail] = useState("");
  const [uploading, setUploading] = useState(false);
  const [newTuto, setNewTuto] = useState({ title: "", body: "", category: "" });
  const [newRequest, setNewRequest] = useState("");
  const [requireApproval, setRequireApproval] = useState(false);

  const load = useCallback(async () => {
    const pid = project.id;
    const [st, ac, dl, fr, ms, mem, tut] = await Promise.all([
      supabase.from("stages").select("*").eq("project_id", pid).order("position"),
      supabase.from("action_items").select("*").eq("project_id", pid).order("position").order("created_at"),
      supabase.from("deliverables").select("*").eq("project_id", pid).order("created_at"),
      supabase.from("file_requests").select("*").eq("project_id", pid).order("created_at", { ascending: false }),
      supabase.from("messages").select("*").eq("project_id", pid).order("created_at"),
      supabase.from("project_members").select("*").eq("project_id", pid),
      supabase.from("tutorials").select("*").order("position").order("created_at"),
    ]);
    const act = await supabase.from("activity").select("*").eq("project_id", pid).order("created_at", { ascending: false }).limit(12);
    setActivity(act.data || []);
    setStages(st.data || []); setActions(ac.data || []); setDeliverables(dl.data || []);
    setFileRequests(fr.data || []); setMessages(ms.data || []); setMembers(mem.data || []); setTutorials(tut.data || []);
    setStatus(project.status); setSiteUrl(project.site_url || ""); setDevisStatus(project.devis_status || "aucun");
  }, [project.id, project.status, project.site_url, project.devis_status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "messages") { const now = Date.now(); setSeenAt(now); try { localStorage.setItem(seenKey, String(now)); } catch {} }
  }, [tab, messages.length, seenKey]);

  const changeStatus = async (s) => { setStatus(s); await supabase.from("projects").update({ status: s }).eq("id", project.id); };
  const saveSiteUrl = async () => { await supabase.from("projects").update({ site_url: siteUrl.trim() || null }).eq("id", project.id); };
  const changeDevis = async (v) => { setDevisStatus(v); await supabase.from("projects").update({ devis_status: v }).eq("id", project.id); };
  const cycleStage = async (stage) => {
    if (!canManage) return;
    const next = STAGE_NEXT[stage.status] || "a_faire";
    setStages((s) => s.map((x) => (x.id === stage.id ? { ...x, status: next } : x)));
    await supabase.from("stages").update({ status: next, updated_at: new Date().toISOString() }).eq("id", stage.id);
  };
  const toggleAction = async (a) => {
    setActions((x) => x.map((i) => (i.id === a.id ? { ...i, done: !i.done } : i)));
    await supabase.from("action_items").update({ done: !a.done }).eq("id", a.id);
  };
  const addAction = async (e) => { e.preventDefault(); if (!newAction.trim()) return;
    const { data } = await supabase.from("action_items").insert({ project_id: project.id, label: newAction.trim() }).select().single();
    if (data) setActions((x) => [...x, data]); setNewAction(""); };
  const delAction = async (id) => { await supabase.from("action_items").delete().eq("id", id); setActions((x) => x.filter((i) => i.id !== id)); };
  const sendMessage = async (e) => { e.preventDefault(); if (!msg.trim()) return; const body = msg.trim(); setMsg("");
    const { data } = await supabase.from("messages").insert({ project_id: project.id, sender_id: profile.id, body }).select().single();
    if (data) setMessages((m) => [...m, data]); };

  const uploadFile = async (file) => {
    if (!file) return; setUploading(true);
    const safe = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `${project.id}/${Date.now()}-${safe}`;
    const { error } = await supabase.storage.from("project-files").upload(path, file);
    if (!error) {
      const { data } = await supabase.from("deliverables").insert({ project_id: project.id, name: file.name, storage_path: path, file_type: file.type, size_bytes: file.size, requires_approval: requireApproval }).select().single();
      if (data) setDeliverables((d) => [...d, data]);
    }
    setUploading(false);
  };
  const addLink = async (e) => { e.preventDefault(); if (!newLink.name.trim() || !newLink.url.trim()) return;
    const { data } = await supabase.from("deliverables").insert({ project_id: project.id, name: newLink.name.trim(), url: newLink.url.trim(), requires_approval: requireApproval }).select().single();
    if (data) setDeliverables((d) => [...d, data]); setNewLink({ name: "", url: "" }); };
  const download = async (d) => {
    if (d.storage_path) { const { data } = await supabase.storage.from("project-files").createSignedUrl(d.storage_path, 3600); if (data?.signedUrl) window.open(data.signedUrl, "_blank"); }
    else if (d.url) window.open(d.url, "_blank");
  };
  const delDeliverable = async (d) => {
    if (d.storage_path) await supabase.storage.from("project-files").remove([d.storage_path]);
    await supabase.from("deliverables").delete().eq("id", d.id);
    setDeliverables((x) => x.filter((i) => i.id !== d.id));
  };
  const approve = async (d) => {
    await supabase.from("deliverables").update({ approved: true, approved_at: new Date().toISOString() }).eq("id", d.id);
    setDeliverables((x) => x.map((i) => (i.id === d.id ? { ...i, approved: true } : i)));
  };

  // Demandes de fichiers
  const addRequest = async (e) => { e.preventDefault(); if (!newRequest.trim()) return;
    const { data } = await supabase.from("file_requests").insert({ project_id: project.id, label: newRequest.trim() }).select().single();
    if (data) setFileRequests((r) => [data, ...r]); setNewRequest(""); };
  const delRequest = async (id) => { await supabase.from("file_requests").delete().eq("id", id); setFileRequests((r) => r.filter((x) => x.id !== id)); };
  const fulfillRequest = async (request, file) => {
    if (!file) return; setUploading(true);
    const safe = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `${project.id}/${Date.now()}-${safe}`;
    const { error } = await supabase.storage.from("project-files").upload(path, file);
    if (!error) {
      const { data } = await supabase.from("deliverables").insert({ project_id: project.id, name: `${request.label} — ${file.name}`, storage_path: path, file_type: file.type, size_bytes: file.size }).select().single();
      if (data) setDeliverables((d) => [...d, data]);
      await supabase.from("file_requests").update({ status: "fulfilled", fulfilled_at: new Date().toISOString() }).eq("id", request.id);
      setFileRequests((r) => r.map((x) => (x.id === request.id ? { ...x, status: "fulfilled" } : x)));
    }
    setUploading(false);
  };

  const assignDev = async (e) => { e.preventDefault(); if (!devEmail.trim()) return;
    const { data } = await supabase.from("project_members").insert({ project_id: project.id, dev_email: devEmail.trim().toLowerCase() }).select().single();
    if (data) setMembers((m) => [...m, data]); setDevEmail(""); };
  const addTuto = async (e) => { e.preventDefault(); if (!newTuto.title.trim()) return;
    const { data } = await supabase.from("tutorials").insert({ title: newTuto.title.trim(), body: newTuto.body.trim() || null, category: newTuto.category.trim() || null }).select().single();
    if (data) setTutorials((t) => [...t, data]); setNewTuto({ title: "", body: "", category: "" }); };
  const delTuto = async (id) => { await supabase.from("tutorials").delete().eq("id", id); setTutorials((t) => t.filter((x) => x.id !== id)); };

  const doneStages = stages.filter((s) => s.status === "termine").length;
  const pct = stages.length ? Math.round((doneStages / stages.length) * 100) : 0;
  const openActions = actions.filter((a) => !a.done).length;
  const unread = messages.filter((m) => m.sender_id !== profile.id && new Date(m.created_at).getTime() > seenAt).length;
  const pending = fileRequests.filter((r) => r.status === "pending");
  const toValidate = deliverables.filter((d) => d.requires_approval && !d.approved).length;
  const validated = deliverables.filter((d) => d.approved).length;
  const badgeFor = (id) => (id === "afaire" ? openActions : id === "fichiers" ? (pending.length + toValidate) : id === "messages" ? unread : 0);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* SIDEBAR */}
      <aside className="lg:w-64 shrink-0">
        <div className="bento p-4 lg:sticky lg:top-28">
          <div className="px-2 pt-2 pb-4 border-b border-white/[0.06] mb-3">
            <div className="font-display text-lg tracking-tight leading-tight truncate">{project.name}</div>
            <div className="mt-1.5 inline-flex rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-[var(--accent)]">
              {PROJECT_STATUS.find((s) => s.id === status)?.label || status}
            </div>
          </div>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {NAV.map((n) => {
              const Icon = n.icon; const active = tab === n.id; const b = badgeFor(n.id);
              return (
                <button key={n.id} onClick={() => setTab(n.id)}
                  className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors shrink-0 lg:w-full ${active ? "bg-[var(--accent)]/[0.12] border border-[var(--accent)]/30" : "border border-transparent hover:bg-white/[0.03]"}`}>
                  <span className={`shrink-0 ${active ? "text-[var(--accent)]" : "text-zinc-500 group-hover:text-zinc-300"}`}><Icon className="w-5 h-5" strokeWidth={1.7} /></span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className={`text-sm ${active ? "text-white" : "text-zinc-300"}`}>{n.label}</span>
                      {b > 0 && <span className="rounded-full bg-[var(--accent)] text-black text-[10px] font-bold px-1.5 leading-4">{b}</span>}
                    </span>
                    <span className="hidden lg:block text-[11px] text-zinc-500 leading-tight">{n.desc}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* CONTENU */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Bandeau avancement */}
        <div className="bento p-6 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{NAV.find((n) => n.id === tab)?.label}</div>
              <p className="text-zinc-500 text-sm mt-1">{project.client_email}{project.secteur ? ` · ${project.secteur}` : ""}</p>
            </div>
            {canManage && (
              <select className="field bg-transparent w-auto" value={status} onChange={(e) => changeStatus(e.target.value)}>
                {PROJECT_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-black">{s.label}</option>)}
              </select>
            )}
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2"><span>Avancement</span><span>{pct}%</span></div>
            <div className="h-1.5 w-full rounded-full bg-white/[0.08] overflow-hidden"><div className="h-full bg-[var(--accent)] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          </div>
        </div>

        {/* APERÇU */}
        {tab === "apercu" && (
          <div className="space-y-6">
            <div className="bento p-6 sm:p-7">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Les étapes</h3>
              <div className="space-y-3">
                {stages.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3 last:border-0">
                    <span className="text-sm text-zinc-200">{s.label}</span>
                    <button onClick={() => cycleStage(s)} disabled={!canManage}
                      className={`shrink-0 rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.15em] transition-colors ${STAGE_COLOR[s.status]} ${canManage ? "cursor-pointer hover:bg-white/[0.04]" : "cursor-default"}`}>
                      {STAGE_LABEL[s.status]}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bento p-5"><div className="font-display text-3xl text-[var(--accent)]">{pct}%</div><div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Avancement</div></div>
              <button onClick={() => setTab("afaire")} className="bento p-5 text-left hover:border-[var(--accent)]/40 transition-colors">
                <div className="font-display text-3xl">{openActions}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Action(s) à faire</div>
              </button>
              <button onClick={() => setTab("fichiers")} className="bento p-5 text-left hover:border-[var(--accent)]/40 transition-colors">
                <div className="font-display text-3xl">{deliverables.length}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Fichier(s){validated > 0 ? ` · ${validated} validé${validated > 1 ? "s" : ""}` : ""}</div>
              </button>
              <button onClick={() => setTab("fichiers")} className="bento p-5 text-left hover:border-[var(--accent)]/40 transition-colors">
                <div className="font-display text-3xl text-[var(--accent)]">{toValidate}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">À valider</div>
              </button>
            </div>
            <div className="bento p-6 sm:p-7">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Activité récente</h3>
              <div className="space-y-3">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />
                    <span className="text-zinc-300 flex-1">{a.summary}</span>
                    <span className="text-[11px] text-zinc-500 shrink-0">{fmt(a.created_at)}</span>
                  </div>
                ))}
                {activity.length === 0 && <p className="text-sm text-zinc-500">Aucune activité pour l'instant.</p>}
              </div>
            </div>
            <div className="bento p-6 sm:p-7">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Votre site</h3>
              {canManage ? (
                <div className="flex flex-wrap gap-2">
                  <input className="field flex-1 min-w-[200px]" placeholder="https://votre-site.fr" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
                  <button className="btn-pill" onClick={saveSiteUrl}>Enregistrer</button>
                </div>
              ) : siteUrl ? (
                <a href={siteUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[var(--accent)] hover:underline">Voir mon site en ligne <ExternalLink className="w-4 h-4" /></a>
              ) : <p className="text-sm text-zinc-500">Le lien de votre site apparaîtra ici une fois en ligne.</p>}
            </div>
            {canManage ? (
              <div className="bento p-6 sm:p-7">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Facturation & devis</h3>
                <select className="field bg-transparent w-auto" value={devisStatus} onChange={(e) => changeDevis(e.target.value)}>
                  {DEVIS_STATUS.map((d) => <option key={d.id} value={d.id} className="bg-black">{d.label}</option>)}
                </select>
                <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">La facturation se gère sur Indy. Ici tu suis l'état, et tu peux envoyer le devis (PDF) au client via l'onglet Fichiers.</p>
              </div>
            ) : (devisStatus !== "aucun" && (
              <div className="bento p-6 sm:p-7">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Paiement</h3>
                <p className="text-sm text-zinc-300 mb-4">Statut : <span className="text-white">{DEVIS_STATUS.find((d) => d.id === devisStatus)?.label}</span></p>
                {devisStatus === "acompte_a_payer" && <button onClick={() => changeDevis("acompte_paye")} className="btn-pill btn-pill-accent text-sm">J'ai payé l'acompte</button>}
                {devisStatus === "solde_a_payer" && <button onClick={() => changeDevis("solde_paye")} className="btn-pill btn-pill-accent text-sm">J'ai payé le solde</button>}
                {devisStatus === "acompte_paye" && <span className="inline-flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Acompte réglé — en attente du solde</span>}
                {devisStatus === "solde_paye" && <span className="inline-flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Réglé en totalité</span>}
                <p className="text-[11px] text-zinc-500 mt-3">Indiquez ici quand vous avez réglé — l'agence est notifiée.</p>
              </div>
            ))}
            {isAdmin && (
              <div className="bento p-6 sm:p-7">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Développeurs assignés</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {members.map((m) => <span key={m.id} className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300">{m.dev_email}</span>)}
                  {members.length === 0 && <span className="text-sm text-zinc-500">Aucun développeur assigné.</span>}
                </div>
                <form onSubmit={assignDev} className="flex flex-wrap gap-2">
                  <input className="field flex-1 min-w-[180px]" type="email" placeholder="email@developpeur.fr" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} />
                  <button className="btn-pill" type="submit"><UserPlus className="w-4 h-4" /> Assigner</button>
                </form>
              </div>
            )}
          </div>
        )}

        {/* À FAIRE */}
        {tab === "afaire" && (
          <div className="bento p-6 sm:p-7">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Ce qu'on attend de vous</h3>
            <div className="space-y-2">
              {actions.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[var(--bg-soft)] px-4 py-3">
                  <button onClick={() => toggleAction(a)} className="shrink-0">
                    {a.done ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Circle className="w-5 h-5 text-zinc-500" />}
                  </button>
                  <span className={`flex-1 text-sm ${a.done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{a.label}</span>
                  {canManage && <button onClick={() => delAction(a.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {actions.length === 0 && <p className="text-sm text-zinc-500">Rien à faire pour l'instant — on vous préviendra ici.</p>}
            </div>
            {canManage && (
              <form onSubmit={addAction} className="mt-4 flex gap-2">
                <input className="field flex-1" placeholder="Ex. Envoyer vos photos, valider la maquette…" value={newAction} onChange={(e) => setNewAction(e.target.value)} />
                <button className="btn-pill" type="submit"><Plus className="w-4 h-4" /></button>
              </form>
            )}
          </div>
        )}

        {/* FICHIERS */}
        {tab === "fichiers" && (
          <div className="space-y-6">
            {/* Demandes de fichiers */}
            {(pending.length > 0 || canManage) && (
              <div className="bento p-6 sm:p-7">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5 flex items-center gap-2"><Inbox className="w-4 h-4" /> Fichiers demandés</h3>
                <div className="space-y-2.5">
                  {pending.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl border border-[var(--accent)]/25 bg-[var(--accent)]/[0.05] px-4 py-3">
                      <span className="flex-1 text-sm text-zinc-200">{r.label}</span>
                      {canValidate ? (
                        <label className={`btn-pill btn-pill-accent text-xs cursor-pointer ${uploading ? "opacity-60" : ""}`}>
                          <Upload className="w-3.5 h-3.5" /> {uploading ? "Envoi…" : "Envoyer"}
                          <input type="file" className="hidden" disabled={uploading} onChange={(e) => fulfillRequest(r, e.target.files?.[0])} />
                        </label>
                      ) : (
                        <span className="text-[11px] uppercase tracking-wider text-zinc-500">En attente</span>
                      )}
                      {canManage && <button onClick={() => delRequest(r.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  {pending.length === 0 && <p className="text-sm text-zinc-500">Aucune demande en attente.</p>}
                </div>
                {canManage && (
                  <form onSubmit={addRequest} className="mt-4 flex gap-2">
                    <input className="field flex-1" placeholder="Demander un fichier au client (ex. logo, photos…)" value={newRequest} onChange={(e) => setNewRequest(e.target.value)} />
                    <button className="btn-pill" type="submit"><Inbox className="w-4 h-4" /> Demander</button>
                  </form>
                )}
              </div>
            )}

            {/* Livrables */}
            <div className="bento p-6 sm:p-7">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Fichiers & livrables</h3>
              <div className="space-y-2.5">
                {deliverables.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[var(--bg-soft)] px-4 py-3">
                    <FileText className="w-4 h-4 text-[var(--accent)] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{d.name}</div>
                      <div className="text-[11px] text-zinc-500">{d.storage_path ? human(d.size_bytes) : "Lien externe"} · {fmt(d.created_at)}</div>
                    </div>
                    <button onClick={() => download(d)} className="btn-pill text-xs">{d.storage_path ? <Download className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />} Ouvrir</button>
                    {d.approved ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs shrink-0"><CheckCircle2 className="w-4 h-4" /> Validé</span>
                    ) : d.requires_approval ? (
                      canValidate ? (
                        <button onClick={() => approve(d)} className="btn-pill btn-pill-accent text-xs shrink-0"><Check className="w-3.5 h-3.5" /> Valider</button>
                      ) : (
                        <span className="rounded-full border border-[var(--accent)]/40 px-2.5 py-1 text-[10px] uppercase tracking-wider text-[var(--accent)] shrink-0">À valider</span>
                      )
                    ) : null}
                    {canManage && <button onClick={() => delDeliverable(d)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
                {deliverables.length === 0 && <p className="text-sm text-zinc-500">Aucun fichier pour l'instant.</p>}
              </div>
              {canManage && (
                <div className="mt-5 space-y-3 border-t border-white/[0.06] pt-5">
                  <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer select-none">
                    <input type="checkbox" checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} className="w-4 h-4 accent-[var(--accent)]" />
                    Demander la validation du client (sinon, simple fichier à consulter)
                  </label>
                  <label className={`btn-pill cursor-pointer ${uploading ? "opacity-60" : ""}`}>
                    <Upload className="w-4 h-4" /> {uploading ? "Envoi…" : "Envoyer un fichier (PDF, image, vidéo…)"}
                    <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} disabled={uploading} />
                  </label>
                  <form onSubmit={addLink} className="flex flex-wrap gap-2">
                    <input className="field flex-1 min-w-[140px]" placeholder="Nom du lien" value={newLink.name} onChange={(e) => setNewLink((v) => ({ ...v, name: e.target.value }))} />
                    <input className="field flex-1 min-w-[140px]" placeholder="URL (site, vidéo YouTube, Drive…)" value={newLink.url} onChange={(e) => setNewLink((v) => ({ ...v, url: e.target.value }))} />
                    <button className="btn-pill" type="submit"><Link2 className="w-4 h-4" /> Ajouter un lien</button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MESSAGERIE */}
        {tab === "messages" && (
          <div className="bento p-6 sm:p-7">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Messagerie</h3>
            <div className="space-y-4 max-h-[28rem] overflow-y-auto mb-4 pr-1">
              {messages.map((m) => {
                const mine = m.sender_id === profile.id;
                const who = mine ? "Vous" : (canManage ? (project.client_email || "Client") : "ML Agence");
                return (
                  <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[var(--accent)] text-black" : "bg-[var(--bg-soft)] border border-white/[0.06] text-zinc-200"}`}>{m.body}</div>
                    <span className="text-[10px] text-zinc-500 mt-1">{who} · {fmt(m.created_at)}</span>
                  </div>
                );
              })}
              {messages.length === 0 && <p className="text-sm text-zinc-500">Aucun message. Démarrez la conversation.</p>}
            </div>
            <form onSubmit={sendMessage} className="flex gap-2">
              <input className="field flex-1" placeholder="Votre message…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input" />
              <button className="btn-pill btn-pill-accent" type="submit"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        )}

        {/* PRISE EN MAIN */}
        {tab === "priseenmain" && (
          <div className="space-y-4">
            {tutorials.map((t) => (
              <div key={t.id} className="bento p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {t.category && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">{t.category}</span>}
                    <h3 className="font-display text-xl tracking-tight mt-1">{t.title}</h3>
                  </div>
                  {isAdmin && <button onClick={() => delTuto(t.id)} className="text-zinc-600 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>}
                </div>
                {t.body && <p className="text-sm text-zinc-400 leading-relaxed mt-3">{t.body}</p>}
                {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:underline">En savoir plus <ArrowUpRight className="w-4 h-4" /></a>}
              </div>
            ))}
            {tutorials.length === 0 && <div className="bento p-6"><p className="text-sm text-zinc-500">Les guides de prise en main apparaîtront ici.</p></div>}
            {isAdmin && (
              <form onSubmit={addTuto} className="bento p-6 space-y-3">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Ajouter un guide (visible par tous les clients)</div>
                <div className="flex flex-wrap gap-2">
                  <input className="field flex-1 min-w-[160px]" placeholder="Titre" value={newTuto.title} onChange={(e) => setNewTuto((v) => ({ ...v, title: e.target.value }))} />
                  <input className="field w-40" placeholder="Catégorie" value={newTuto.category} onChange={(e) => setNewTuto((v) => ({ ...v, category: e.target.value }))} />
                </div>
                <textarea className="field" rows={3} placeholder="Contenu du guide…" value={newTuto.body} onChange={(e) => setNewTuto((v) => ({ ...v, body: e.target.value }))} />
                <button className="btn-pill" type="submit"><Plus className="w-4 h-4" /> Ajouter</button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
